import { memo, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
   Button,
   Chip,
   Div,
   eventStopPropagation,
   filterHover,
   Image,
   lightenColor,
   Loader,
   Text,
   Tooltip,
   useLoader,
   useMediaQuery,
   useTheme,
   type TooltipRef,
} from "react-better-html";

import { dealTypeData } from "../../constants/properties";

import { type PropertyFull } from "../../types/property";

import { formatPrice, getPropertyTitle } from "../../utils/functions";
import { useI18n } from "../../utils/i18n";
import { useReduxAction } from "../../utils/hooks";

import ConfirmationWrapper from "../common/ConfirmationWrapper";

type TextDotProps = {
   color?: React.CSSProperties["color"];
};

const TextDot = memo(function TextDot({ color }: TextDotProps) {
   const theme = useTheme();

   return <Div width={4} height={4} backgroundColor={color ?? theme.colors.textPrimary} borderRadius={999} />;
});

type PropertyCardProps = {
   property: PropertyFull;
   isLoading?: boolean;
   withActionsMenu?: boolean;
   onClick?: (property: PropertyFull, event: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
};

function PropertyCard({ property, isLoading, withActionsMenu, onClick }: PropertyCardProps) {
   const theme = useTheme();
   const { t } = useI18n();
   const mediaQuery = useMediaQuery();
   const navigate = useNavigate();
   const propertyOperationsIsLoading = useLoader(`properties:propertyOperations${property.property.id}`);

   const menuTooltipRef = useRef<TooltipRef>(null);

   const propertyOperations = useReduxAction((actions) => actions.properties.propertyOperations);

   const onClickOpen = useCallback(() => {
      menuTooltipRef.current?.close();
      navigate(`/properties/${property.property.id}`);
   }, [property.property.id]);
   const onClickOpenInNewTab = useCallback(() => {
      menuTooltipRef.current?.close();
      window.open(`/properties/${property.property.id}`, "_blank");
   }, [property.property.id]);
   const onClickArchive = useCallback(() => {
      menuTooltipRef.current?.close();

      propertyOperations({
         id: property.property.id,
         data: {
            operation: "ARCHIVE",
         },
      });
   }, [property.property.id]);
   const onCancelArchive = useCallback(() => {
      menuTooltipRef.current?.close();
   }, []);

   const isLoadingReady = isLoading || propertyOperationsIsLoading;

   return (
      <Div.box
         position="relative"
         width="100%"
         padding={0}
         boxShadowHover={onClick ? theme.styles.boxShadow : undefined}
         overflow="hidden"
         cursor={onClick ? "pointer" : undefined}
         value={property}
         onClickWithValue={onClick}
         pointerEvents={isLoadingReady ? "none" : undefined}
         userSelect={isLoadingReady ? "none" : undefined}
      >
         <Div.column position="relative" height="100%">
            <Div position="relative" width="100%" height={mediaQuery.size600 ? 180 : 240} flexShrink={0}>
               <Image src={property.property.mainImage?.url} width="100%" height="100%" objectFit="cover" />

               {withActionsMenu && (
                  <Div
                     position="absolute"
                     top={theme.styles.gap}
                     right={theme.styles.gap}
                     onClick={eventStopPropagation}
                  >
                     <Tooltip
                        content={
                           <Div width={210}>
                              <Tooltip.item
                                 icon="arrowBendDownLeft"
                                 text={t((l) => l.components.propertyCard.menuActions.open)}
                                 onClick={onClickOpen}
                              />
                              <Tooltip.item
                                 icon="externalLink"
                                 text={t((l) => l.components.propertyCard.menuActions.openInNewTab)}
                                 onClick={onClickOpenInNewTab}
                              />
                              <ConfirmationWrapper onContinue={onClickArchive} onCancel={onCancelArchive}>
                                 <Tooltip.item
                                    icon="archive"
                                    text={t((l) => l.components.propertyCard.menuActions.archive)}
                                    textColor={theme.colors.error}
                                    iconColor={theme.colors.error}
                                 />
                              </ConfirmationWrapper>
                           </Div>
                        }
                        asContextMenu
                        withArrow
                        align="right"
                        trigger="click"
                        ref={menuTooltipRef}
                     >
                        <Button.icon
                           icon="threeDotsHorizontal"
                           backgroundColor={theme.colors.backgroundBase}
                           backgroundColorHover={theme.colors.backgroundBase}
                           filterHover={filterHover().z1}
                           size={12}
                           iconSize={18}
                        />
                     </Tooltip>
                  </Div>
               )}
            </Div>

            <Div.box height="100%" border="none" backgroundColor="transparent" borderRadius={0}>
               <Div.column height="100%">
                  <Div.row alignItems="center" gap={theme.styles.gap} marginBottom={theme.styles.gap / 2}>
                     <Text flex={1} fontSize={22} fontWeight={700}>
                        {formatPrice(property.property.lowestActivePrice ?? 0, property.property.lowestActiveCurrency)}
                     </Text>

                     <Chip
                        fontSize={14}
                        fontWeight={700}
                        text={t((l) => l.enums.dealType[property.property.dealType])}
                        color={dealTypeData[property.property.dealType].color}
                        backgroundColor={lightenColor(dealTypeData[property.property.dealType].color, 0.9)}
                     />
                  </Div.row>

                  <Text fontSize={14} fontWeight={700}>
                     {getPropertyTitle(property.property)}
                  </Text>

                  <Div.row alignItems="center" gap={theme.styles.gap} marginTop={theme.styles.gap}>
                     <Text fontSize={14} color={theme.colors.textSecondary}>
                        {property.property.areaSqm ?? 0}m²
                     </Text>
                     <TextDot color={theme.colors.textSecondary} />
                     <Text fontSize={14} color={theme.colors.textSecondary}>
                        {property.property.bedrooms ?? 0}{" "}
                        {t((l) => l.common[property.property.bedrooms === 1 ? "bed" : "beds"])}
                     </Text>
                     <TextDot color={theme.colors.textSecondary} />
                     <Text fontSize={14} color={theme.colors.textSecondary}>
                        {t((l) => l.common.floor)} {property.property.floor ?? 0}
                        {property.property.floorsTotal !== undefined ? `/${property.property.floorsTotal ?? 0}` : ""}
                     </Text>
                  </Div.row>

                  <Div.row alignItems="center" justifyContent="flex-end" gap={theme.styles.gap} marginTop="auto">
                     <Text fontSize={12} color={theme.colors.textSecondary}>
                        {t((l) =>
                           property.property.availability
                              ? l.enums.propertyAvailability[property.property.availability]
                              : l.enums.propertyAvailability.UNKNOWN,
                        )}
                     </Text>
                  </Div.row>
               </Div.column>
            </Div.box>
         </Div.column>

         <Div.row
            position="absolute"
            width="100%"
            height="100%"
            top={0}
            left={0}
            backgroundColor={theme.colors.label + "80"}
            opacity={!isLoadingReady ? 0 : undefined}
            alignItems="center"
            justifyContent="center"
            pointerEvents="none"
            transition={theme.styles.transition}
         >
            <Loader.box color={theme.colors.base} />
         </Div.row>
      </Div.box>
   );
}

export default memo(PropertyCard);
